import axios from 'axios'

/** i18next `t` funksiyasining bu yerda kerak bo'lgan qismi */
export type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * Xato tanasi.
 *
 * Asosiy (va yagona to'g'ri) shakl — envelope:
 *   `{"success":false,"error":{"code":404,"tag":"NOT_FOUND","text":"Abonent topilmadi"}}`
 *
 * ⚠️ Eski maydonlar (`detail`, `message`, `title`) ATAYLAB saqlanib
 * qoldi. Ular endi backend'dan kelmaydi, lekin zanjirda backend'dan
 * boshqa manbalar ham bor: nginx, proksi, brauzer kengaytmasi. Ularni
 * olib tashlash "xato bor, sababi noma'lum" degan foydasiz xabarga olib
 * kelardi — aynan tushuntirish kerak bo'lgan paytda.
 */
interface ApiErrorBody {
  error?: { code?: number; tag?: string; text?: string } | null
  message?: string
  detail?: string
  title?: string
  code?: string
}

/**
 * Server matnlari YOZILGAN til.
 *
 * ⚠️ Bu taxmin emas, o'lchangan holat: backendda i18n YO'Q. Har bir
 * `XsibErrors.of(...)` chaqiruvi matnni o'zbekcha qatorda beradi
 * (`CommonExceptionHandler`, `GatewayResponses`, domen servislari).
 * Ya'ni panel inglizchada bo'lsa ham server "Abonent topilmadi" deb
 * javob qaytaradi — X-SIB-002 ning ILDIZI shu, va u SERVER tarafda.
 */
const SERVER_TEXT_LANGUAGE = 'uz'

/**
 * `XsibApiError` (`shared/api/client.ts`) ni IMPORTSIZ aniqlaydi.
 *
 * ⚠️ NEGA `instanceof` EMAS. `client.ts` `sessionRefresh` → `tokenStorage`
 * zanjirini tortadi; bu modul esa `DataTable` dan tortib login sahifasigacha
 * hamma joyda import qilinadi. `errors.ts → client.ts` bog'lanishi shu
 * zanjirni HTTP klientsiz ham kerak bo'lgan joylarga sudrab kelardi va
 * modul yuklanish tartibiga sezgir aylana yaratish xavfi bor edi.
 * `name` esa konstruktorda ATAYLAB qo'yilgan barqaror belgi.
 */
interface TaggedError extends Error {
  tag?: string | null
  code?: number | null
}

function taggedError(error: unknown): TaggedError | null {
  return error instanceof Error && error.name === 'XsibApiError' ? error : null
}

/**
 * Xatoning HTTP holati — aniqlab bo'lmasa `null`.
 *
 * ⚠️ SABABNI AJRATISH uchun: «ruxsat yo'q» (403) va «yozuv yo'q» (404)
 * ekranda BIR XIL ko'rinmasligi kerak. `translateError` ikkalasini ham
 * jumlaga aylantiradi, ya'ni shoxlanish uchun yaramaydi.
 *
 * Ikki manba tekshiriladi: odatdagi axios javobi va envelope ichida
 * `success:false` bilan kelgan `XsibApiError` (`client.ts`). Ikkinchisi
 * hozir faqat 2xx yo'lida uchraydi, lekin u yerda ham `code` HAQIQIY
 * holatni olib yuradi va uni tashlab yuborish jimgina noto'g'ri
 * shoxlanish berardi.
 */
export function errorStatus(error: unknown): number | null {
  if (axios.isAxiosError(error)) return error.response?.status ?? null
  return taggedError(error)?.code ?? null
}

/**
 * Tag oilalari — `CONSTRAINT_23505`, `REMOTE_SERVICE_LEDGER`, `DB_08006`
 * kabi kodlar SON yoki SERVIS NOMI bilan tugaydi, ya'ni ularning har bir
 * ko'rinishiga alohida kalit yozib bo'lmaydi. Oila prefiksi bo'yicha
 * bitta tarjima olinadi.
 *
 * ⚠️ `CONSTRAINT_23505` (yagonalik buzilishi) ALOHIDA qoladi — u operator
 * o'zi tuzata oladigan yagona baza xatosi ("bunday login bor"), qolgan
 * `CONSTRAINT_*` esa dasturchi ishi.
 */
export function errorTag(error: unknown): string | null {
  if (axios.isAxiosError<ApiErrorBody>(error)) return error.response?.data?.error?.tag ?? null
  return taggedError(error)?.tag ?? null
}

const TAG_FAMILIES =['CONSTRAINT_', 'REMOTE_SERVICE_', 'DB_'] as const

function tagKey(tag: string): string {
  const exact = `common.errorTag.${tag}`
  const family = TAG_FAMILIES.find((prefix) => tag.startsWith(prefix))
  return family && tag !== 'CONSTRAINT_23505' ? `common.errorTag.${family}` : exact
}

/**
 * Tanish `tag` ni panel tiliga o'giradi. Tarjimasi bo'lmasa `null`.
 *
 * `defaultValue: ''` — i18next kalit topilmasa KALITNING O'ZINI qaytaradi
 * ("common.errorTag.FOO"), ya'ni tekshiruvsiz ishlatish ekranga texnik
 * kalit chiqarardi.
 */
function translateTag(tag: string | null | undefined, t: Translate): string | null {
  if (!tag) return null
  const text = t(tagKey(tag), { defaultValue: '' })
  return text === '' ? null : text
}

/**
 * Server matnlari faqat o'zbekcha, shuning uchun ular faqat o'zbekcha
 * panelda ko'rsatiladi. Boshqa tilda operator tarjimani ko'radi: yarim
 * inglizcha, yarim o'zbekcha xabar hech kimga tushunarli emas.
 */
function speaksServerLanguage(t: Translate): boolean {
  return t('common.langCode', { defaultValue: '' }) === SERVER_TEXT_LANGUAGE
}

function localServerText(serverText: string | undefined, t: Translate): string | undefined {
  return serverText && speaksServerLanguage(t) ? serverText : undefined
}

/**
 * Backend xatosini foydalanuvchiga ko'rsatiladigan matnga aylantiradi.
 *
 * O'zbekcha panelda server matni o'zi chiqadi. Boshqa tilda: `tag`
 * tarjimasi, u bo'lmasa HTTP holatiga mos umumiy gap. Server
 * xabarlarini tillarga o'tkazish backend ishi (`Accept-Language`).
 */
export function translateError(error: unknown, t: Translate): string {
  if (axios.isAxiosError<ApiErrorBody>(error)) {
    const body = error.response?.data
    const serverText = body?.error?.text ?? body?.message ?? body?.detail

    const local = localServerText(serverText, t)
    if (local) return local
    const translated = translateTag(body?.error?.tag, t)
    if (translated) return translated

    if (!error.response) return t('errors.network')
    const status = error.response.status

    // ⚠️ 401 BU YERDA "sessiya tugadi" DEB TARJIMA QILINMAYDI.
    // xsib-core ushlanmagan har qanday xatoni (noto'g'ri UUID, validatsiya
    // xatosi, noma'lum yo'l) TANASIZ 401 ga aylantiradi — `client.ts` dagi
    // batafsil izohga qarang. Haqiqatan muddati o'tgan token bo'lganda
    // interceptor allaqachon kirish sahifasiga olib o'tgan bo'lardi, ya'ni
    // bu yergacha yetib kelgan 401 deyarli har doim server rad javobi.
    if (status === 400 || status === 401) return t('errors.serverRejected')
    if (status === 403) return t('errors.forbidden')
    if (status === 404) return t('errors.notFound')
    if (status === 409) return t('errors.conflict')
    if (status === 422) return t('errors.unprocessable')
    return t('errors.withCode', { status })
  }

  // Envelope ichida `success:false` bilan kelgan xato — `client.ts`
  // interceptori uni `XsibApiError` ga o'girgan, `tag` esa saqlangan.
  const tagged = taggedError(error)
  if (tagged) {
    return (
      localServerText(tagged.message, t) ??
      translateTag(tagged.tag, t) ??
      t('errors.generic')
    )
  }

  /*
    ⚠️ XOM JavaScript XATOSI EKRANGA CHIQMAYDI — X-SIB-015.

    Ilgari bu yerda `error.message` qaytarilardi va natijada operator
    "Cannot read properties of undefined (reading 'body')" degan matnni
    sahifadagi qizil bannerda ko'rardi. Bu ikki tomondan zarar:
      · foydalanuvchi uchun ma'nosiz va qo'rqinchli;
      · dasturchi uchun ham foydasiz, chunki STEK yo'q — faqat bitta qator.

    Endi stek konsolga, foydalanuvchiga esa tushunarli jumla boradi.
    ⚠️ `ZodError` ham shu yo'ldan o'tadi: uning `message` i bir necha
    yuz belgilik JSON bo'lib, ekranga sig'masdi.
  */
  if (error instanceof Error) {
    console.error('[xsib] kutilmagan xato:', error)
    return t('common.errorUnexpected')
  }
  return t('errors.generic')
}
